var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";
import isObject from "lodash/isObject";
import set from "lodash/set";
import { CREATE, DELETE, GET_LIST, GET_MANY, GET_MANY_REFERENCE, GET_ONE, UPDATE, } from "react-admin";
import { buildWhere } from "./buildWhere";
import exhaust from "./utils/exhaust";
import getFinalType from "./utils/getFinalType";
var ModifiersParams;
(function (ModifiersParams) {
    ModifiersParams["connect"] = "connect";
    ModifiersParams["create"] = "create";
    ModifiersParams["createMany"] = "createMany";
    ModifiersParams["delete"] = "delete";
    ModifiersParams["deleteMany"] = "deleteMany";
    ModifiersParams["disconnect"] = "disconnect";
    ModifiersParams["set"] = "set";
    ModifiersParams["update"] = "update";
    ModifiersParams["updateMany"] = "updateMany";
    ModifiersParams["upsert"] = "upsert";
    ModifiersParams["connectOrCreate"] = "connectOrCreate";
})(ModifiersParams || (ModifiersParams = {}));
var buildOrderBy = function (introspectionResults, resource, sort) {
    if (!sort)
        return null;
    var field = sort.field, order = sort.order;
    var orderType = introspectionResults.types.find(function (t) { return t.name === resource.type.name + "OrderByWithRelationInput"; });
    var fieldParts = field === null || field === void 0 ? void 0 : field.split(".");
    if (!orderType || !fieldParts) {
        return null;
    }
    if (!orderType.inputFields.some(function (f) { return f.name === fieldParts[0]; })) {
        return null;
    }
    var selector = {};
    set(selector, field, sort.order === "ASC" ? "asc" : "desc");
    return [selector];
};
var buildGetListVariables = function (introspectionResults) { return function (resource, aorFetchType, params) {
    var where = buildWhere(params.filter, resource, introspectionResults);
    return {
        skip: (params.pagination.page - 1) * params.pagination.perPage,
        take: params.pagination.perPage,
        orderBy: buildOrderBy(introspectionResults, resource, params === null || params === void 0 ? void 0 : params.sort),
        where: where,
    };
}; };
var buildGetOneVariables = function (introspectionResults) { return function (resource, params) {
    var type = introspectionResults.types.find(function (t) { return t.name === resource.type.name + "WhereUniqueInput"; });
    var idType = type === null || type === void 0 ? void 0 : type.inputFields.find(function (f) { return f.name === "id"; });
    if (idType.type.kind === "SCALAR" && idType.type.name === "String") {
        return {
            where: {
                id: String(params.id),
            },
        };
    }
    if (idType.type.kind === "SCALAR" && idType.type.name === "Int") {
        return {
            where: {
                id: parseInt(params.id),
            },
        };
    }
    // should usually not happen
    return {
        where: {
            id: params.id,
        },
    };
}; };
var getCreateInputDataTypeForList = function (createModifier, introspectionResults) {
    var createListModifierType = createModifier.type;
    var createInputFieldType = createListModifierType.ofType.kind === "NON_NULL"
        ? createListModifierType.ofType.ofType
        : createListModifierType.ofType;
    return introspectionResults.types.find(
    // what about a nullable type?
    function (t) { return t.name === createInputFieldType.name; });
};
var getUpdateInputDataTypeForList = function (updateModifier, introspectionResults) {
    var updateListModifierType = updateModifier.type;
    var updateInputFieldType = getFinalType(updateListModifierType.ofType);
    var updateListInputDataType = introspectionResults.types.find(function (introdspectionType) {
        return introdspectionType.name === updateInputFieldType.name;
    }).inputFields.find(function (input) { return input.name === "data"; }).type;
    return introspectionResults.types.find(function (introdspectionType) {
        return introdspectionType.name === updateListInputDataType.ofType.name;
    });
};
function isObjectWithId(data) {
    return Boolean(data === null || data === void 0 ? void 0 : data.id);
}
var buildNewInputValue = function (fieldData, previousFieldData, fieldName, fieldType, introspectionResults) {
    var kind = fieldType.kind;
    switch (kind) {
        case "SCALAR":
        case "ENUM": {
            if (fieldType.kind === "SCALAR" && fieldType.name === "String") {
                if (isObject(fieldData)) {
                    return JSON.stringify(fieldData);
                }
            }
            return fieldData;
        }
        case "INPUT_OBJECT": {
            var fieldObjectType_1 = fieldType;
            var fullFieldObjectType = introspectionResults.types.find(function (t) { return t.name === fieldObjectType_1.name; });
            var setModifier = fullFieldObjectType === null || fullFieldObjectType === void 0 ? void 0 : fullFieldObjectType.inputFields.find(function (i) { return i.name === ModifiersParams.set; });
            var connectModifier = fullFieldObjectType === null || fullFieldObjectType === void 0 ? void 0 : fullFieldObjectType.inputFields.find(function (i) { return i.name === ModifiersParams.connect; });
            var disconnectModifier = fullFieldObjectType === null || fullFieldObjectType === void 0 ? void 0 : fullFieldObjectType.inputFields.find(function (i) { return i.name === ModifiersParams.disconnect; });
            if (setModifier && !connectModifier && !disconnectModifier) {
                return { set: fieldData };
            }
            var isRelationship = fullFieldObjectType === null || fullFieldObjectType === void 0 ? void 0 : fullFieldObjectType.inputFields.every(function (i) {
                return Object.keys(ModifiersParams).includes(i.name);
            });
            // is it a relation?
            if (isRelationship) {
                // if it has a set modifier, it is an update array
                var createModifier = fullFieldObjectType === null || fullFieldObjectType === void 0 ? void 0 : fullFieldObjectType.inputFields.find(function (i) { return i.name === ModifiersParams.create; });
                var updateModifier = fullFieldObjectType === null || fullFieldObjectType === void 0 ? void 0 : fullFieldObjectType.inputFields.find(function (i) { return i.name === ModifiersParams.update; });
                var isList = fullFieldObjectType === null || fullFieldObjectType === void 0 ? void 0 : fullFieldObjectType.inputFields.some(function (i) {
                    return i.type.kind === "LIST";
                });
                if (isList) {
                    if (Array.isArray(fieldData)) {
                        var createListInputType_1 = getCreateInputDataTypeForList(createModifier, introspectionResults);
                        var updateListInputType_1 = updateModifier
                            ? getUpdateInputDataTypeForList(updateModifier, introspectionResults)
                            : null;
                        var variables = fieldData.reduce(function (inputs, referencedField) {
                            if (isObject(referencedField)) {
                                // TODO: we assume "data.id" to be the id
                                if (isObjectWithId(referencedField)) {
                                    var connectRelation = !(previousFieldData === null || previousFieldData === void 0 ? void 0 : previousFieldData.find(function (p) {
                                        return p.id
                                            ? p.id === referencedField.id
                                            : p === referencedField.id;
                                    }));
                                    if (!updateListInputType_1 || connectRelation) {
                                        inputs.connect = __spreadArrays((inputs.connect || []), [
                                            { id: referencedField.id },
                                        ]);
                                    }
                                    else {
                                        // update
                                        var data = buildData(updateListInputType_1, {
                                            id: referencedField.id,
                                            data: referencedField,
                                            previousData: previousFieldData === null || previousFieldData === void 0 ? void 0 : previousFieldData.find(function (previousField) {
                                                // TODO: we assume ".id" to be the id
                                                return previousField.id === referencedField.id;
                                            }),
                                        }, introspectionResults);
                                        if (Object.keys(data).length) {
                                            inputs.update = __spreadArrays((inputs.update || []), [
                                                { where: { id: referencedField.id }, data: data },
                                            ]);
                                        }
                                    }
                                }
                                else {
                                    // create
                                    var data = buildData(createListInputType_1, {
                                        data: referencedField,
                                    }, introspectionResults);
                                    inputs.create = __spreadArrays((inputs.create || []), [data]);
                                }
                            }
                            else {
                                // only reference id's
                                // what about multiple id's for one reference?
                                if (!(previousFieldData === null || previousFieldData === void 0 ? void 0 : previousFieldData.find(function (p) {
                                    // TODO: we assume "p.id" to be the id
                                    return p.id
                                        ? p.id === referencedField
                                        : p === referencedField;
                                }))) {
                                    inputs.connect = __spreadArrays((inputs.connect || []), [
                                        { id: referencedField },
                                    ]);
                                }
                            }
                            return inputs;
                        }, {});
                        // disconnect the ones that are not referenced anymore
                        if (Array.isArray(previousFieldData)) {
                            var disconnect = previousFieldData.reduce(function (inputs, data) {
                                // TODO: we assume "data.id" to be the id
                                var dataId = data.id || data;
                                if (!fieldData.find(function (p) {
                                    // TODO: we assume "p.id" to be the id
                                    var pId = p.id || p;
                                    return pId === dataId;
                                })) {
                                    return __spreadArrays((inputs || []), [{ id: dataId }]);
                                }
                                return inputs;
                            }, []);
                            if (disconnect.length && disconnectModifier) {
                                variables.disconnect = disconnect;
                            }
                        }
                        return variables;
                    }
                    else {
                        throw new Error(fieldName + " should be an array");
                    }
                }
                else {
                    if (!fieldData) {
                        return !disconnectModifier
                            ? undefined
                            : {
                                disconnect: true,
                            };
                    }
                    if (isObject(fieldData)) {
                        if (!isObjectWithId(fieldData)) {
                            if (!createModifier) {
                                return;
                            }
                            // TODO: we assume ".id" to be the id
                            var createObjectModifierType_1 = getFinalType(createModifier.type);
                            var createObjectInputType = introspectionResults.types.find(function (t) { return t.name === createObjectModifierType_1.name; });
                            // create
                            var data = buildData(createObjectInputType, {
                                data: fieldData,
                            }, introspectionResults);
                            return { create: data };
                        }
                        else {
                            if ((previousFieldData === null || previousFieldData === void 0 ? void 0 : previousFieldData.id) === fieldData.id) {
                                if (!updateModifier) {
                                    return;
                                }
                                var updateObjectModifierType_1 = getFinalType(updateModifier.type);
                                var updateObjectInputType = introspectionResults.types.find(function (t) { return t.name === updateObjectModifierType_1.name; });
                                // update
                                var data = buildData(updateObjectInputType, {
                                    id: fieldData.id,
                                    data: fieldData,
                                    previousData: previousFieldData,
                                }, introspectionResults);
                                return { update: data };
                            }
                            else if (connectModifier) {
                                return { connect: { id: fieldData.id } };
                            }
                        }
                    }
                    else if (connectModifier) {
                        return { connect: { id: fieldData } };
                    }
                }
            }
            else {
                return fieldData;
            }
            return;
        }
        case "LIST":
        case "NON_NULL":
            return;
        default:
            exhaust(kind);
    }
};
var buildData = function (inputType, params, introspectionResults) {
    if (!inputType) {
        return {};
    }
    return inputType.inputFields.reduce(function (acc, field) {
        var _a;
        var _b, _c, _d;
        var key = field.name;
        var fieldType = field.type.kind === "NON_NULL" ? field.type.ofType : field.type;
        var fieldData = params.data[key];
        //console.log(key, fieldData, fieldType);
        var previousFieldData = (_d = (_c = (_b = params) === null || _b === void 0 ? void 0 : _b.previousData) === null || _c === void 0 ? void 0 : _c[key]) !== null && _d !== void 0 ? _d : null;
        // TODO in case the content of the array has changed but not the array itself?
        if (isEqual(fieldData, previousFieldData) ||
            (isNil(previousFieldData) && isNil(fieldData))) {
            return acc;
        }
        var newVaue = buildNewInputValue(params.data[key], previousFieldData, field.name, fieldType, introspectionResults);
        return __assign(__assign({}, acc), (_a = {}, _a[key] = newVaue, _a));
    }, {});
};
var buildUpdateVariables = function (introspectionResults) { return function (resource, params, parentResource) {
    var _a, _b;
    var inputType = introspectionResults.types.find(function (t) { return t.name === resource.type.name + "UpdateInput"; });
    var id = (_a = params.id) !== null && _a !== void 0 ? _a : params.data.id; // TODO: do we still need params.data.id?
    delete params.data.id;
    (_b = params.previousData) === null || _b === void 0 ? true : delete _b.id;
    return {
        where: {
            id: id,
        },
        data: buildData(inputType, params, introspectionResults),
    };
}; };
var buildCreateVariables = function (introspectionResults) { return function (resource, params, parentResource) {
    var inputType = introspectionResults.types.find(function (t) { return t.name === resource.type.name + "CreateInput"; });
    var variables = {
        data: buildData(inputType, params, introspectionResults),
    };
    return variables;
}; };
export default (function (introspectionResults) { return function (resource, aorFetchType, params) {
    var _a;
    switch (aorFetchType) {
        case GET_LIST: {
            return buildGetListVariables(introspectionResults)(resource, aorFetchType, params);
        }
        case GET_MANY:
            return {
                where: {
                    //@ts-ignore
                    id: {
                        in: params.ids
                            .map(function (obj) { return (isObject(obj) ? obj.id : obj); })
                            .filter(function (v) { return !isNil(v); }),
                    },
                },
            };
        case GET_MANY_REFERENCE: {
            return buildGetListVariables(introspectionResults)(resource, GET_LIST, __assign(__assign({}, params), { filter: (_a = {}, _a[params.target] = params.id, _a) }));
        }
        case GET_ONE: {
            return buildGetOneVariables(introspectionResults)(resource, params);
        }
        case UPDATE: {
            return buildUpdateVariables(introspectionResults)(resource, params);
        }
        case CREATE: {
            return buildCreateVariables(introspectionResults)(resource, params);
        }
        case DELETE:
            return {
                where: { id: params.id },
            };
    }
}; });
//# sourceMappingURL=buildVariables.js.map